import { useMemo } from 'react';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';
import { isBrowser } from '@/utils/util_helpers/ssr';
import { DEFAULT_CONTENT_WIDTH_IN_NUM } from '@/features/marketplace/constants';
import {
  BREAKPOINT_MOBILE,
  BREAKPOINT_LARGE,
  MOBILE_PADDING,
  VIEWPORT_PADDING_PERCENTAGE,
} from '../carousel-constants';

/**
 * Shared hook for calculating responsive horizontal padding for carousels.
 *
 * SSR-safe: returns MOBILE_PADDING during server-side rendering to prevent
 * hydration mismatch (useWindowDimensions returns 1024 on SSR, which would
 * produce a different padding value than on the actual client device).
 *
 * Breakpoint logic:
 * - Below 576px (xs): fixed 24px padding
 * - 576px – 1625px: 10% of viewport width
 * - Above 1625px: centers content at 1300px max width
 */
export const useCarouselPadding = (): number => {
  const {
    windowDimensions: { width: screenWidth },
  } = useWindowDimensions();

  return useMemo(() => {
    if (!isBrowser() || !screenWidth || screenWidth === 0) {
      return MOBILE_PADDING;
    }

    if (screenWidth < BREAKPOINT_MOBILE) {
      return MOBILE_PADDING;
    }

    if (screenWidth < BREAKPOINT_LARGE) {
      return screenWidth * VIEWPORT_PADDING_PERCENTAGE;
    }

    return Math.max((screenWidth - DEFAULT_CONTENT_WIDTH_IN_NUM) / 2, MOBILE_PADDING);
  }, [screenWidth]);
};
